import React from "react"
import type { OverlayRenderProps } from "react-photo-view/dist/types"
import { isIOS, isMobile } from "../../utils/detectDevice"
import { Button } from "@/components/ui/button"
import { DownloadIcon } from "@radix-ui/react-icons"
import type { Message } from "../../reducers/messagesSlice"
import useHasFileBeenShared from "../../hooks/useHasFileBeenShared"
import useHandleDownload from "../../hooks/useHandleDownload"
import useHandleShareLink from "../../hooks/useHandleShareLink"
import useDownloadFileName from "../../hooks/useDownloadFileName"
import ImageShareButton from "./ImageShareButton"
import classNames from "classnames"
import MessageHelpers from "../../lib/messageHelpers"
import CMRMetaDataDrawer from "@/components/cmr/CMRMetaDataDrawer"

type ImageWithMessage = OverlayRenderProps["images"][number] & {
  message?: Message
}

export const ImageBottomToolbar = ({ images, index }: OverlayRenderProps) => {
  const image = images[index] as ImageWithMessage | undefined
  const message = image?.message
  if (!message) return null
  return <ImageBottomToolbarContent message={message} url={image?.src ?? ""} />
}

const ImageBottomToolbarContent = ({
  message,
  url,
}: {
  message: Message
  url: string
}) => {
  const hasFileBeenShared = useHasFileBeenShared(message)
  const handleShareLink = useHandleShareLink(message)
  const handleDownload = useHandleDownload()
  const downloadFileName = useDownloadFileName(message)
  return (
    <div
      className={classNames(
        "absolute bottom-0 right-0 z-50 flex w-full flex-row justify-end gap-2 bg-black/50 p-2 text-sm text-slate-300",
        { "min-h-20": isIOS },
      )}
    >
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-md bg-black/60 p-2 text-white mix-blend-color">
        {MessageHelpers.timestamp(message)}
      </div>
      <div className="flex w-full flex-row gap-2">
        <div className="flex-1" />
        {!isMobile && handleDownload && downloadFileName && (
          <a
            href={url}
            download
            onClick={(e) => {
              e.preventDefault()
              handleDownload(url, downloadFileName!)
            }}
          >
            <Button variant="ghost" size="icon">
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </a>
        )}
        {isMobile && url && (
          <a href={url} download target="_blank" rel="noreferrer">
            <Button variant="ghost" size="icon">
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </a>
        )}
        {handleShareLink && downloadFileName && (
          <ImageShareButton
            hasFileBeenShared={hasFileBeenShared}
            handleShareLink={handleShareLink}
            downloadFileName={downloadFileName}
          />
        )}
        <CMRMetaDataDrawer message={message} />
      </div>
    </div>
  )
}
